import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import { Switch, IconButton } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import "components/Screens/Customers/CustomerDetials/CustomerProfile/AllowCreditCard/AllowCreditCard.scss";

import Modal from "components/common/Modal";
import Text from "components/common/Text";
import Button from "components/common/Button";
import RadioButton from "components/common/RadioButtons";
import Chip from "components/common/Chip";

import { getDropdownData } from "selectors/dropdownData";
import { DropdownData as GetDropdownData } from "store/actions/DropdownData";
import { GetCustomerDetails } from "store/actions/Customers/GetCustomerDetails";
import { updateAllowCreditCardOption, updateSkipPaymentOption } from "services/Admin/Dashboard";
import { formatPaymentDateMonth } from "util/utils";

import { CREDIT_CARD_REASONS_LIST } from "constants/index";

function AllowCreditCard({ customerId, customerDetails, setAllowCreditCard, hasPaymentMethod, isAddedCreditCard }) {
  const [modalState, setModalState] = useState({
    show: false,
    selectedReason: "",
    otherReason: "",
    surchargeAmount: "",
    surchargeType: "Percentage",
  });
  const [skipPaymentMethod, setSkipPaymentMethod] = useState(customerDetails?.skipPayment || false);

  const [skipModalState, setSkipModalState] = useState({
    show: false,
    loading: false,
    selectedReason: "",
    otherReason: "",
  });

  const { skipPayment, creditCardSettings, reason, reasonNote } = customerDetails || {};
  const {
    allowCreditCard,
    reason: creditCardReason,
    notes,
    surchargeValue,
    surchargeType: currentSurchargeType,
    updatedAt,
    Edge_Employee: edgeEmployee,
  } = creditCardSettings || {};
  const { First_Name: firstName, Last_Name: lastName } = edgeEmployee || {};

  useEffect(() => {
    setSkipPaymentMethod(skipPayment || false);
  }, [skipPayment, allowCreditCard]);

  const dropdownData: any = useSelector(getDropdownData);
  const { creditCardReasons, skipPaymentReasons } = dropdownData || {};
  const dispatch = useDispatch();

  useEffect(() => {
    if (!creditCardReasons) {
      dispatch(GetDropdownData());
    }
  }, [creditCardReasons, dispatch]);

  const handleUpdateAllowCreditCardOption = async (allowCreditCardValue) => {
    if (!allowCreditCardValue) {
      const body = { allowCreditCard: false };
      await updateAllowCreditCardOption(customerId, body);
      setAllowCreditCard(false);
      dispatch(GetCustomerDetails({ customerId }));
      return;
    }

    if (allowCreditCard) {
      setModalState({
        show: true,
        selectedReason: creditCardReason || "",
        otherReason: notes || "",
        surchargeAmount: surchargeValue || "",
        surchargeType: currentSurchargeType === "Percentage" ? "Percentage" : "Flat_Rate",
      });
    } else {
      setModalState({
        show: true,
        selectedReason: "",
        otherReason: "",
        surchargeAmount: "",
        surchargeType: "Percentage",
      });
    }
  };

  const onSubmit = async () => {
    setModalState((prev) => ({ ...prev, show: false }));

    const body = {
      allowCreditCard: true,
      reason: modalState.selectedReason === "Other" ? "Other" : modalState.selectedReason,
      notes: modalState.selectedReason === "Other" ? modalState.otherReason : "",
      surchargeType: modalState.surchargeType === "Percentage" ? "Percentage" : "Flat_Rate",
      surchargeValue: parseFloat(modalState.surchargeAmount) || 0,
    };

    await updateAllowCreditCardOption(customerId, body);
    setAllowCreditCard(true);
    dispatch(GetCustomerDetails({ customerId }));
  };

  const onClose = () => {
    setModalState((prev) => ({ ...prev, show: false }));
  };

  const updateSkipPaymentMethodOption = async (skipPaymentValue) => {
    setSkipModalState((prev) => ({ ...prev, loading: true }));
    try {
      if (!skipPaymentValue) {
        const body = { skipPayment: false };
        await updateSkipPaymentOption(customerId, body, "v2");
        setSkipPaymentMethod(false);
        dispatch(GetCustomerDetails({ customerId }));
        return;
      }
      setSkipModalState((prev) => ({
        ...prev,
        selectedReason: "",
        otherReason: "",
        show: true,
      }));
    } finally {
      setSkipModalState((prev) => ({ ...prev, loading: false }));
    }
  };

  const onSkipSubmit = async () => {
    setSkipModalState((prev) => ({ ...prev, loading: true }));
    try {
      setSkipModalState((prev) => ({ ...prev, show: false }));
      const body = {
        skipPayment: true,
        reason: skipModalState.selectedReason === "Other" ? "Other" : skipModalState.selectedReason,
        reasonNote: skipModalState.selectedReason === "Other" ? skipModalState.otherReason : "",
      };
      await updateSkipPaymentOption(customerId, body, "v2");
      setSkipPaymentMethod(true);
      dispatch(GetCustomerDetails({ customerId }));
    } finally {
      setSkipModalState((prev) => ({ ...prev, loading: false }));
    }
  };

  const onSkipClose = () => {
    setSkipModalState((prev) => ({ ...prev, show: false }));
  };

  const isSkipModalSubmitDisabled =
    skipModalState.selectedReason === "" ||
    (skipModalState.selectedReason === "Other" && !skipModalState.otherReason.trim());
  const isMainModalSubmitDisabled =
    modalState.selectedReason === "" ||
    (modalState.selectedReason === "Other" && !modalState.otherReason.trim()) ||
    !modalState.surchargeAmount;

  return (
    <>
      {skipModalState.show && (
        <Modal
          open={skipModalState.show}
          onClose={onSkipClose}
          title="Skip Add Payment Method"
          width="480px"
          titleClass=""
          sx={{}}
        >
          <form onSubmit={onSkipSubmit}>
            <div>
              <div className="para-body-m-regular color-text-body-secondary">Please choose a reason to continue:</div>
              <div className="mt-1">
                {skipPaymentReasons?.map((option) => (
                  <RadioButton
                    key={option.value}
                    label={option.label}
                    value={option.value}
                    name="skip-reason"
                    checked={skipModalState.selectedReason === option.value}
                    onChange={() => {
                      setSkipModalState((prev) => ({
                        ...prev,
                        selectedReason: option.value,
                        otherReason: option.value !== "Other" ? "" : prev.otherReason,
                      }));
                    }}
                  />
                ))}
              </div>
            </div>
            {skipModalState.selectedReason === "Other" && (
              <div>
                <div className="para-body-m-medium specify-reason mt-025">Specify reason</div>
                <Text
                  multiline
                  name="other-reason"
                  value={skipModalState.otherReason}
                  onChange={(e) => setSkipModalState((prev) => ({ ...prev, otherReason: e.target.value }))}
                  placeholder="Enter your reason"
                  fullWidth
                  size="small"
                  label=""
                  helperText=""
                  type="text"
                  sx={{}}
                  isOptional={false}
                  minValue={0}
                  error={false}
                  variant="outlined"
                  startIcon={null}
                  endIcon={null}
                  labelIcon={null}
                  rows={3}
                  maxRows={3}
                  minRows={1}
                />
              </div>
            )}
            <div className="flex justify-end gap-1 mt-05">
              <Button label="Cancel" size="large" variant="text" color="primary" onClick={onSkipClose} />
              <Button
                label="Confirm"
                variant="contained"
                size="large"
                color="primary"
                className="submit-button"
                onClick={onSkipSubmit}
                isDisabled={isSkipModalSubmitDisabled}
                type="submit"
              />
            </div>
          </form>
        </Modal>
      )}

      {modalState.show && (
        <Modal
          open={modalState.show}
          onClose={onClose}
          title="Credit/Debit Card Payment Method"
          width="552px"
          titleClass="mb-1"
          sx={{}}
        >
          <form onSubmit={onSubmit} className="credit-card-modal-form">
            <div className="modal-section surcharge-section">
              <div className="mb-05">
                <p className="section-title">Surcharge</p>
              </div>
              <div className="surcharge-input-row">
                <div className="surcharge-type-selection">
                  <div className="toggle-switch-container">
                    <div
                      className={`toggle-option ${modalState.surchargeType === "Percentage" ? "active" : ""}`}
                      onClick={() => setModalState((prev) => ({ ...prev, surchargeType: "Percentage" }))}
                    >
                      <span className="toggle-option-text">Percentage</span>
                    </div>
                    <div
                      className={`toggle-option ${modalState.surchargeType === "Flat_Rate" ? "active" : ""}`}
                      onClick={() => setModalState((prev) => ({ ...prev, surchargeType: "Flat_Rate" }))}
                    >
                      <span className="toggle-option-text">Flat amount</span>
                    </div>
                  </div>
                </div>
                <div className="surcharge-amount-input">
                  <Text
                    name="surcharge-amount"
                    value={modalState.surchargeAmount}
                    onChange={(e) => setModalState((prev) => ({ ...prev, surchargeAmount: e.target.value }))}
                    placeholder="Enter amount"
                    fullWidth
                    size="small"
                    label=""
                    helperText=""
                    type="number"
                    isOptional={false}
                    minValue={0}
                    error={false}
                    variant="outlined"
                    startIcon={modalState.surchargeType === "Percentage" ? <PercentIcon /> : <AttachMoneyIcon />}
                    sx={{
                      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                        color: "var(--color-icon-secondary)",
                      },
                    }}
                    endIcon={null}
                    labelIcon={null}
                    maxRows={1}
                    minRows={1}
                  />
                </div>
              </div>
            </div>

            <div className="modal-section reason-section">
              <div className="mb-05">
                <h6 className="section-title">Reason</h6>
              </div>
              <div className="reason-selection">
                <div className="radio-group-container">
                  {CREDIT_CARD_REASONS_LIST?.map((option) => (
                    <RadioButton
                      key={option.value}
                      label={option.label}
                      value={option.value}
                      name="credit-card-reason"
                      checked={modalState.selectedReason === option.value}
                      onChange={() => {
                        setModalState((prev) => ({
                          ...prev,
                          selectedReason: option.value,
                          otherReason: option.value !== "Other" ? "" : prev.otherReason,
                        }));
                      }}
                    />
                  ))}
                </div>
                {modalState.selectedReason === "Other" && (
                  <div className="notes-container">
                    <Text
                      multiline
                      name="other-reason"
                      value={modalState.otherReason}
                      onChange={(e) => setModalState((prev) => ({ ...prev, otherReason: e.target.value }))}
                      placeholder="Enter your reason here..."
                      fullWidth
                      size="small"
                      label="Specify reason"
                      helperText=""
                      type="text"
                      sx={{}}
                      isOptional={false}
                      minValue={0}
                      error={false}
                      variant="outlined"
                      startIcon={null}
                      endIcon={null}
                      labelIcon={null}
                      rows={3}
                      maxRows={3}
                      minRows={1}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <Button label="Cancel" size="large" variant="text" color="primary" onClick={onClose} />
              <Button
                label="Confirm"
                variant="contained"
                size="large"
                color="primary"
                className="ml-05"
                onClick={onSubmit}
                isDisabled={isMainModalSubmitDisabled}
                type="submit"
              />
            </div>
          </form>
        </Modal>
      )}

      <div className="payment-information-container">
        <div className="payment-methods-section">
          <div className="payment-method-row">
            <div className="payment-method-left">
              <p className="payment-method-label">Credit/Debit Card Payment Method</p>
              {allowCreditCard && surchargeValue && (
                <Chip
                  label={`${currentSurchargeType === "Percentage" ? `${surchargeValue}%` : `$${surchargeValue}`} surcharge`}
                  size="small"
                  color="default"
                  variant="filled"
                  labelClass=""
                  onClick={null}
                  onDelete={null}
                />
              )}
            </div>
            <div className="payment-method-right">
              {isAddedCreditCard ? (
                <IconButton
                  onClick={() => handleUpdateAllowCreditCardOption(true)}
                  title="Edit credit card settings"
                  aria-label="Edit credit card settings"
                  size="small"
                  className="edit-icon-button"
                >
                  <EditOutlinedIcon className="edit-icon" />
                </IconButton>
              ) : (
                <>
                  {allowCreditCard && (
                    <IconButton
                      onClick={() => handleUpdateAllowCreditCardOption(true)}
                      title="Edit credit card settings"
                      aria-label="Edit credit card settings"
                      size="small"
                      className="edit-icon-button"
                    >
                      <EditOutlinedIcon className="edit-icon" />
                    </IconButton>
                  )}
                  <Switch
                    size="small"
                    checked={allowCreditCard || false}
                    onChange={(event) => handleUpdateAllowCreditCardOption(event.target.checked)}
                  />
                </>
              )}
            </div>
          </div>

          {/* eslint-disable */}
          
          {allowCreditCard && (
          <div className="payment-details">
               
            <p className="detail-reason">
              {creditCardReason === "Other" && notes
                ? `Other: ${notes}`
                : creditCardReason || "Mid-Market Customer"
              }
            </p>
            <p className="detail-last-updated">
              Last updated by <span className="detail-name">{firstName} {lastName}</span> on{" "}
              <span className="detail-date">
                {(() => {
                  const dateString = updatedAt || new Date().toISOString().split('T')[0];
                  const date = new Date(dateString);
                  if (isNaN(date.getTime())) {
                    const currentDate = new Date().toISOString().split('T')[0];
                    return formatPaymentDateMonth(currentDate, true);
                  }
                  
                  const formattedDate = date.toISOString().split('T')[0];
                  return formatPaymentDateMonth(formattedDate, true);
                })()}
              </span>
            </p>
          </div>
        )}

          {/* eslint-enable */}
          {!hasPaymentMethod && (
            <>
              <div className="payment-method-row mt-1">
                <div className="payment-method-left">
                  <p className="payment-method-label">Skip Payment Method in Onboarding</p>
                </div>
                <div className="payment-method-right">
                  <Switch
                    size="small"
                    checked={skipPaymentMethod}
                    onChange={(event) => updateSkipPaymentMethodOption(event.target.checked)}
                    disabled={skipModalState.loading}
                  />
                </div>
              </div>
              {reason && (
                <div className="text-left">
                  <p className="color-text-body-secondary">
                    <span className="reason-heading">Reason: </span>
                    <span className="reason-text">{reason}</span>
                  </p>
                  <p className="reason-note color-text-body-primary">{reasonNote}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

AllowCreditCard.propTypes = {
  customerId: PropTypes.string.isRequired,
  setAllowCreditCard: PropTypes.func.isRequired,
  hasPaymentMethod: PropTypes.bool.isRequired,
  isAddedCreditCard: PropTypes.bool.isRequired,
  customerDetails: PropTypes.shape({
    creditCardSettings: PropTypes.shape({
      allowCreditCard: PropTypes.bool,
      reason: PropTypes.string,
      notes: PropTypes.string,
      surchargeType: PropTypes.string,
      surchargeValue: PropTypes.string,
      updatedAt: PropTypes.string,
      Edge_Employee: PropTypes.shape({
        First_Name: PropTypes.string,
        Last_Name: PropTypes.string,
        Email: PropTypes.string,
      }),
    }),
  }),
};

export default AllowCreditCard;
